import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiDownload } from 'react-icons/fi';
import { jwtDecode } from 'jwt-decode';
import usStates from '../../us-states.json';
import { isFluxDomain } from '../../utils/fluxTheme';
import './AgentDashboard.css';

function AgentOpportunities({ token }) {
  const isFlux = isFluxDomain();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [loading, setLoading] = useState(false);
  const [isGroupMember, setIsGroupMember] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // OTP modal state
  const [otpModal, setOtpModal] = useState({ open: false, step: 'idle', maskedPhone: '', code: '', error: '', sending: false, verifying: false });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    state: '',
    search: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    try {
      const decoded = jwtDecode(token);
      setIsGroupMember(!!decoded.groupLocationId);
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchLeads();
  }, [pagination.page, filters.startDate, filters.endDate, filters.state, filters.search]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await axios.get(`/api/agent/distribution/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLeads(response.data.leads || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0
      }));
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ startDate: '', endDate: '', state: '', search: '' });
    setSearchInput('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    // Debounce 400ms before firing the real filter
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: val }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 400);
  };

  const handlePdfDownload = async (leadEmail) => {
    if (!leadEmail) { alert('No email available for this opportunity'); return; }
    try {
      const response = await axios.get(
        `/api/agent/distribution/lead-pdf/${encodeURIComponent(leadEmail)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.pdfUrl) {
        // Use first+last name in the downloaded filename
        const { pdfUrl, firstName, lastName } = response.data;
        const safeName = (s) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '_');
        let filename;
        if (firstName || lastName) {
          filename = `${safeName(firstName)}_${safeName(lastName)}.pdf`;
        } else {
          filename = `${safeName(leadEmail)}.pdf`;
        }
        // Trigger download with proper filename via anchor
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = filename;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert('PDF not found for this opportunity');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      if (error.response?.status === 404) {
        alert('PDF not available for this opportunity');
      } else {
        alert('Error fetching PDF. Please try again.');
      }
    }
  };

  const toggleSelectLead = (leadEmail) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadEmail)) next.delete(leadEmail);
      else next.add(leadEmail);
      return next;
    });
  };

  // Toggle select all ON CURRENT PAGE — preserves selections from other pages
  const toggleSelectCurrentPage = () => {
    const pageEmails = leads.map(l => l.leadEmail).filter(Boolean);
    const allPageSelected = pageEmails.every(e => selectedLeads.has(e));
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageEmails.forEach(e => next.delete(e)); // deselect this page
      } else {
        pageEmails.forEach(e => next.add(e));    // add this page to existing selection
      }
      return next;
    });
  };

  const isCurrentPageAllSelected = leads.length > 0 && leads.map(l => l.leadEmail).filter(Boolean).every(e => selectedLeads.has(e));

  // Step 1: clicking Download — send OTP first
  const handleBulkDownloadClick = async () => {
    if (selectedLeads.size === 0) { alert('Please select at least one lead'); return; }
    if (selectedLeads.size > 50) { alert('Maximum 50 PDFs per bulk download'); return; }

    // Get agent email from token
    let agentEmail = '';
    try { agentEmail = JSON.parse(atob((token || '').split('.')[1])).email || ''; } catch {}

    setOtpModal({ open: true, step: 'sending', maskedPhone: '', code: '', error: '', sending: true, verifying: false });

    try {
      const res = await axios.post('/api/auth/send-login-phone-otp',
        { email: agentEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOtpModal(prev => ({ ...prev, step: 'verify', maskedPhone: res.data.maskedPhone || '***-***-****', sending: false }));
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setOtpModal(prev => ({ ...prev, step: 'error', error: msg, sending: false }));
    }
  };

  // Step 2: verify OTP then run download
  const handleOtpVerify = async () => {
    if (!otpModal.code.trim()) { setOtpModal(prev => ({ ...prev, error: 'Please enter the verification code' })); return; }

    let agentEmail = '';
    try { agentEmail = JSON.parse(atob((token || '').split('.')[1])).email || ''; } catch {}

    setOtpModal(prev => ({ ...prev, verifying: true, error: '' }));

    try {
      await axios.post('/api/auth/verify-login-phone-otp',
        { email: agentEmail, code: otpModal.code.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // OTP verified — close modal and start download
      setOtpModal({ open: false, step: 'idle', maskedPhone: '', code: '', error: '', sending: false, verifying: false });
      await runBulkDownload();
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid code. Please try again.';
      setOtpModal(prev => ({ ...prev, verifying: false, error: msg }));
    }
  };

  // Step 3: actual download after OTP passes
  const runBulkDownload = async () => {
    setBulkDownloading(true);
    try {
      const response = await axios.post(
        '/api/agent/distribution/lead-pdf-bulk',
        { leadEmails: [...selectedLeads] },
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob', timeout: 300000 }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-pdfs-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSelectedLeads(new Set());
    } catch (error) {
      console.error('Error bulk downloading PDFs:', error);
      alert('Error downloading PDFs. Please try again.');
    } finally {
      setBulkDownloading(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="container">
      <div className="dashboard-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h2>Opportunities</h2>
        {selectedLeads.size > 0 && (
          <button
            onClick={handleBulkDownloadClick}
            disabled={bulkDownloading}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 18px', background: bulkDownloading ? '#9ca3af' : (isFlux ? '#3FA45F' : '#4f46e5'),
              color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: bulkDownloading ? 'default' : 'pointer'
            }}
          >
            <FiDownload size={16} />
            {bulkDownloading
              ? 'Downloading…'
              : `Download ${selectedLeads.size} PDF${selectedLeads.size !== 1 ? 's' : ''} as ZIP`}
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{
        background: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginTop: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>Filter Opportunities</h3>

        {/* Search bar */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
            🔍 Search
          </label>
          <div style={{ position: 'relative', maxWidth: 420 }}>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search by email, first name, last name, or phone…"
              style={{
                width: '100%',
                padding: '9px 36px 9px 12px',
                borderRadius: '6px',
                border: `1px solid ${searchInput ? '#6366f1' : '#d1d5db'}`,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
                boxShadow: searchInput ? '0 0 0 3px rgba(99,102,241,0.1)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s'
              }}
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setFilters(prev => ({ ...prev, search: '' })); setPagination(prev => ({ ...prev, page: 1 })); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9ca3af', lineHeight: 1 }}
                title="Clear search"
              >×</button>
            )}
          </div>
          {filters.search && (
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#6366f1' }}>
              Showing results for "<strong>{filters.search}</strong>" — {pagination.total} match{pagination.total !== 1 ? 'es' : ''}
            </p>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>
              State
            </label>
            <select
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', background: 'white' }}
            >
              <option value="">All States</option>
              {usStates.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.code})
                </option>
              ))}
            </select>
          </div>
        </div>
        <button onClick={clearFilters} className="btn btn-sm btn-secondary">
          Clear Filters
        </button>
      </div>

      {/* Leads Table */}
      <div>
        <h3 style={{ marginBottom: '16px' }}>
          My Opportunities ({pagination.total || 0})
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
        ) : leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {filters.search
              ? <>No opportunities matched "<strong>{filters.search}</strong>". Try a different name, email, or phone number.</>
              : 'No opportunities found'}
          </div>
        ) : (
          <>
            {/* Selection banner — shows when leads are selected across pages */}
            {selectedLeads.size > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isFlux ? 'rgba(63,164,95,0.08)' : 'rgba(79,70,229,0.07)', border: `1px solid ${isFlux ? '#3FA45F' : '#4f46e5'}`, borderRadius: 8, padding: '8px 16px', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: isFlux ? '#3FA45F' : '#4f46e5' }}>
                  {selectedLeads.size} lead{selectedLeads.size !== 1 ? 's' : ''} selected across all pages
                </span>
                <button onClick={() => setSelectedLeads(new Set())} style={{ background: 'transparent', border: 'none', fontSize: 12, color: '#6b7280', cursor: 'pointer', textDecoration: 'underline' }}>
                  Clear selection
                </button>
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isCurrentPageAllSelected}
                        onChange={toggleSelectCurrentPage}
                        title={isCurrentPageAllSelected ? 'Deselect this page' : 'Select this page (adds to existing selection)'}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    {isGroupMember && <th>Agent</th>}
                    <th>Name</th>
                    <th>Opportunity Contact</th>
                    <th>State</th>
                    <th>Date</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => {
                    const leadContact = lead.leadEmail || lead.leadPhone || '-';
                    const contactType = lead.leadEmail ? '📧' : (lead.leadPhone ? '📱' : '');
                    const displayName = lead.leadName || [lead.firstName, lead.lastName].filter(Boolean).join(' ') || '—';

                    return (
                      <tr key={index} style={{ background: selectedLeads.has(lead.leadEmail) ? (isFlux ? 'rgba(63,164,95,0.06)' : 'rgba(79,70,229,0.05)') : undefined }}>
                        <td style={{ textAlign: 'center' }}>
                          {lead.leadEmail && (
                            <input
                              type="checkbox"
                              checked={selectedLeads.has(lead.leadEmail)}
                              onChange={() => toggleSelectLead(lead.leadEmail)}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                        </td>
                        {isGroupMember && (
                          <td>
                            <span style={{ fontSize: '13px', color: '#4f46e5' }}>
                              {lead.agentEmail || '-'}
                            </span>
                          </td>
                        )}
                        <td style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>
                          {displayName}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {contactType && <span style={{ fontSize: '14px' }}>{contactType}</span>}
                            {lead.leadEmail ? (
                              <span
                                onClick={() => navigate('/agent/leads', { state: { openEmail: lead.leadEmail } })}
                                style={{
                                  color: isFlux ? '#3FA45F' : '#4f46e5',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dotted',
                                  fontWeight: '500'
                                }}
                                title="Open conversation"
                              >
                                {leadContact}
                              </span>
                            ) : (
                              <span>{leadContact}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="badge">{lead.state}</span>
                        </td>
                        <td>
                          {new Date(lead.timestamp).toLocaleString('en-US', {
                            timeZone: 'America/Chicago',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {/* {(lead.leadEmail && isFluxDomain()) ? ( */}
                            <button
                              onClick={() => handlePdfDownload(lead.leadEmail)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: isFlux ? '#3FA45F' : '#4f46e5',
                                cursor: 'pointer',
                                padding: '6px',
                                borderRadius: '4px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = isFlux ? 'rgba(63, 164, 95, 0.1)' : 'rgba(79, 70, 229, 0.1)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title="Download PDF"
                            >
                              <FiDownload size={18} />
                            </button>
                          {/* ) : (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                          )} */}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '24px'
              }}>
                {totalPages > 1 && (
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="btn btn-sm btn-secondary"
                    style={{ opacity: pagination.page === 1 ? 0.5 : 1 }}
                  >
                    Previous
                  </button>
                )}
                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Showing {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0} –{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} opportunities
                  {totalPages > 1 && ` (Page ${pagination.page} of ${totalPages})`}
                </span>
                {totalPages > 1 && (
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= totalPages}
                    className="btn btn-sm btn-secondary"
                    style={{ opacity: pagination.page >= totalPages ? 0.5 : 1 }}
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── OTP Verification Modal for Bulk PDF Download ── */}
      {otpModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#111827' }}>Verify Your Identity</h3>

            {otpModal.step === 'sending' && (
              <p style={{ color: '#6b7280', fontSize: 14, margin: '12px 0' }}>Sending verification code to your phone…</p>
            )}

            {otpModal.step === 'verify' && (
              <>
                <p style={{ color: '#6b7280', fontSize: 14, margin: '8px 0 20px' }}>
                  We sent a 6-digit code to <strong>{otpModal.maskedPhone}</strong>.<br />
                  Enter it below to download {selectedLeads.size} PDF{selectedLeads.size !== 1 ? 's' : ''}.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={9}
                  placeholder="Enter code"
                  value={otpModal.code}
                  onChange={e => setOtpModal(prev => ({ ...prev, code: e.target.value.replace(/\D/g, ''), error: '' }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleOtpVerify(); }}
                  autoFocus
                  style={{ width: '100%', padding: '12px 16px', fontSize: 24, textAlign: 'center', letterSpacing: 8, border: `2px solid ${otpModal.error ? '#ef4444' : '#d1d5db'}`, borderRadius: 10, outline: 'none', boxSizing: 'border-box', marginBottom: 8, fontWeight: 700 }}
                />
                {otpModal.error && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{otpModal.error}</p>}
                <button
                  onClick={handleOtpVerify}
                  disabled={otpModal.verifying || !otpModal.code.trim()}
                  style={{ width: '100%', padding: '13px', background: otpModal.verifying || !otpModal.code.trim() ? '#9ca3af' : (isFlux ? '#3FA45F' : '#4f46e5'), color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: otpModal.verifying ? 'default' : 'pointer', marginBottom: 10 }}
                >
                  {otpModal.verifying ? 'Verifying…' : 'Verify & Download'}
                </button>
                <button
                  onClick={() => setOtpModal({ open: false, step: 'idle', maskedPhone: '', code: '', error: '', sending: false, verifying: false })}
                  style={{ background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </>
            )}

            {otpModal.step === 'error' && (
              <>
                <p style={{ color: '#ef4444', fontSize: 14, margin: '12px 0 20px' }}>{otpModal.error}</p>
                <button
                  onClick={() => setOtpModal({ open: false, step: 'idle', maskedPhone: '', code: '', error: '', sending: false, verifying: false })}
                  style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentOpportunities;
